/**
 * Card and deck primitives.
 *
 * A card is an opaque **string ID**, never an index into a hand. An index means
 * something different the moment the hand changes; an ID is self-describing and
 * survives a round trip through the wire protocol unchanged (see
 * docs/decisions.md).
 *
 * IDs are structured so they can be parsed, but nothing outside this module
 * should parse them by hand - use `rankOf`, `suitOf` and friends. The format is
 * an implementation detail that multi-deck support has already changed once.
 *
 *   "8H"      eight of hearts
 *   "TS"      ten of spades (T, not 10, so every ID is fixed width)
 *   "JR"      red joker
 *   "8H:1"    eight of hearts from the second deck in a multi-deck game
 */
export const SUITS = ['C', 'D', 'H', 'S'];
/** T is ten. Fixed-width ranks keep IDs sortable and parseable without a delimiter. */
export const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K'];
export const JOKER_RED = 'JR';
export const JOKER_BLACK = 'JB';
const JOKERS = [JOKER_RED, JOKER_BLACK];
const DECK_SEPARATOR = ':';
/** Strip the deck suffix, if any. */
function base(card) {
    /*
     * A nullish card reaching here is a bug somewhere upstream, and it used to
     * surface as "Cannot read property 'indexOf' of undefined" from deep inside
     * a match loop - which names neither the card, the zone, nor the game.
     *
     * Returning an empty string makes the parsers answer null instead of
     * throwing, so a malformed zone degrades to "not a card" rather than killing
     * the room everybody is sitting in.
     */
    if (!card)
        return '';
    const at = card.indexOf(DECK_SEPARATOR);
    return at < 0 ? card : card.substring(0, at);
}
export function isJoker(card) {
    const b = base(card);
    return b === JOKER_RED || b === JOKER_BLACK;
}
/** The rank of a card, or null for a joker. */
export function rankOf(card) {
    if (isJoker(card))
        return null;
    const text = base(card);
    return text.length === 0 ? null : text.charAt(0);
}
/** The suit of a card, or null for a joker. */
export function suitOf(card) {
    if (isJoker(card))
        return null;
    const b = base(card);
    return b.length === 0 ? null : b.charAt(b.length - 1);
}
/** Which deck a card came from, 0-based. Always 0 in a single-deck game. */
export function deckOf(card) {
    const at = card.indexOf(DECK_SEPARATOR);
    return at < 0 ? 0 : parseInt(card.substring(at + 1), 10);
}
/**
 * Two cards that play identically but are not the same physical card.
 *
 * In a multi-deck game the two eights of hearts are interchangeable for every
 * rules purpose and distinct for every conservation purpose - which is exactly
 * why they need distinct IDs and this helper.
 */
export function sameFace(a, b) {
    return base(a) === base(b);
}
export function makeCard(rank, suit, deck = 0) {
    return deck === 0 ? rank + suit : rank + suit + DECK_SEPARATOR + String(deck);
}
/**
 * Build an ordered deck. **Never shuffled here** - shuffling goes through the
 * engine's seeded random API so replays reproduce (plan rule 2.5).
 */
export function buildDeck(options = {}) {
    const decks = options.decks ?? 1;
    const ranks = options.ranks ?? RANKS;
    const suits = options.suits ?? SUITS;
    const cards = [];
    for (let d = 0; d < decks; d++) {
        for (let s = 0; s < suits.length; s++) {
            const suit = suits[s];
            for (let r = 0; r < ranks.length; r++) {
                cards.push(makeCard(ranks[r], suit, d));
            }
        }
        if (options.jokers) {
            for (let j = 0; j < JOKERS.length; j++) {
                const joker = JOKERS[j];
                cards.push(d === 0 ? joker : joker + DECK_SEPARATOR + String(d));
            }
        }
    }
    return cards;
}
/**
 * Rank order for comparisons, ace low by default.
 *
 * Games vary on where the ace sits, so this takes the ordering rather than
 * assuming one: `rankValue(card, ACE_HIGH)`.
 */
export const ACE_LOW = RANKS;
export const ACE_HIGH = [
    '2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A',
];
/**
 * Is this a red card?
 *
 * Hearts and diamonds. Trivial, and here rather than in each game because
 * every patience that builds in alternating colours needs it - Klondike now,
 * and anything like it later - and a second copy is a second place to get the
 * suits the wrong way round.
 *
 * A joker is neither, and answers false rather than throwing: a game that
 * deals jokers into a tableau has a bigger problem than this function.
 */
export function isRed(card) {
    const suit = suitOf(card);
    return suit === 'H' || suit === 'D';
}
/** Do these two cards differ in colour? What alternating tableaux are built on. */
export function opposite(a, b) {
    return isRed(a) !== isRed(b);
}
/** Position of a card's rank in the given order, or -1 for a joker. */
export function rankValue(card, order = ACE_LOW) {
    const rank = rankOf(card);
    if (rank === null)
        return -1;
    return order.indexOf(rank);
}
/** Sort a copy by suit then rank. Presentation only - never reorder a hand in state. */
export function sortCards(cards, order = ACE_LOW) {
    const copy = cards.slice();
    copy.sort(function (a, b) {
        const suitA = suitOf(a);
        const suitB = suitOf(b);
        if (suitA !== suitB) {
            // Jokers (null suit) sort last.
            if (suitA === null)
                return 1;
            if (suitB === null)
                return -1;
            return SUITS.indexOf(suitA) - SUITS.indexOf(suitB);
        }
        const byRank = rankValue(a, order) - rankValue(b, order);
        return byRank !== 0 ? byRank : deckOf(a) - deckOf(b);
    });
    return copy;
}
//# sourceMappingURL=cards.js.map